import { manufacturingFetch } from "./manufacturingClient";

export type MfgOpsDealRow = {
  id: string;
  organization_id: string;
  display_name: string | null;
  status: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type MfgOrdersDealsListResponse = {
  ok: true;
  organization_id: string;
  deals: MfgOpsDealRow[];
};

export function listManufacturingOrdersDeals() {
  return manufacturingFetch<MfgOrdersDealsListResponse>("/api/mfg/orders/deals", { method: "GET" });
}

export type MfgDealRecipeLinkRow = {
  id: string;
  recipe_id: string;
  recipe_code: string;
  recipe_name: string;
  recipe_quantity: number;
  notes: string;
};

export type MfgDealRecipeLinksGetResponse = {
  ok: true;
  organization_id: string;
  deal_id: string;
  links: MfgDealRecipeLinkRow[];
};

export function getManufacturingDealRecipeLinks(dealId: string) {
  const q = new URLSearchParams({ deal_id: dealId });
  return manufacturingFetch<MfgDealRecipeLinksGetResponse>(`/api/mfg/orders/recipe-links?${q.toString()}`, { method: "GET" });
}

export type MfgDealRecipeLinksSetResponse = { ok: true };

export function setManufacturingDealRecipeLinks(payload: {
  deal_id: string;
  links: Array<{ recipe_id: string; recipe_quantity: number; notes?: string }>;
}) {
  return manufacturingFetch<MfgDealRecipeLinksSetResponse>("/api/mfg/orders/recipe-links/set", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type MfgDealMaterialsPreviewResponse = {
  ok: true;
  organization_id: string;
  deal_id: string;
  rollup_mode: "flat_materials" | "nested_materials";
  estimated_materials_cost: number;
  per_recipe: Array<{
    recipe_id: string;
    recipe_code: string;
    recipe_name: string;
    recipe_quantity: number;
    unit_materials_cost: number;
    extended_materials_cost: number;
    rollup_mode: "flat_materials" | "nested_materials";
    resolution_warnings?: string[];
  }>;
  resolved_materials?: Array<{
    material_id: string;
    material_code: string;
    material_name: string;
    quantity: number;
    unit_cost: number;
    extended_cost: number;
  }>;
  resolution_warnings?: string[];
  named_variant_wholesale_price_delta_sum?: number;
  named_variant_retail_price_delta_sum?: number;
  applied_named_variant_application_ids?: string[];
  sale_price_usd: number | null;
  materials_margin_usd: number | null;
};

export type MfgDealCogsSnapshotRow = {
  id: string;
  organization_id: string;
  deal_id: string;
  snapshot_kind: "materials_commit" | "build_commit" | "sale_commit";
  estimated_materials_cost: number;
  resolved_materials: Array<{
    material_id: string;
    material_code: string;
    material_name: string;
    quantity: number;
    unit_cost: number;
    extended_cost: number;
  }>;
  resolution_warnings: string[];
  rollup_mode: string;
  plant_id: string | null;
  build_model_id: string | null;
  materials_cost_usd: number | null;
  labor_cost_usd: number | null;
  build_cost_usd: number | null;
  overhead_rate: number | null;
  applied_overhead_usd: number | null;
  ptl_amount_usd: number | null;
  full_cost_usd: number | null;
  margin_denominators: {
    retail: number | null;
    wholesale: number | null;
    rto: number | null;
    employee: number | null;
  };
  channel_prices_usd: {
    retail: number | null;
    wholesale: number | null;
    rto: number | null;
    employee: number | null;
  };
  precision_tier: string;
  metadata: Record<string, unknown>;
  created_at: string;
  sale_price_usd?: number | null;
  full_cost_margin_usd?: number | null;
  full_cost_margin_pct?: number | null;
  is_legacy_materials_only?: boolean;
};

export type MfgDealFullCogsPreview = MfgDealCogsSnapshotRow & {
  per_recipe: unknown;
  per_line_full_cogs: unknown;
  applied_overhead_note: string;
};

export type MfgDealFullCogsPreviewResponse = {
  ok: true;
  organization_id: string;
  deal_id: string;
  preview: MfgDealFullCogsPreview;
};

export type MfgDealCogsSnapshotGetResponse = {
  ok: true;
  organization_id: string;
  deal_id: string;
  snapshot_kind: string;
  snapshot: MfgDealCogsSnapshotRow | null;
  materials_snapshot: MfgDealCogsSnapshotRow | null;
};

export type MfgDealCogsSnapshotCreateResponse = {
  ok: true;
  organization_id: string;
  deal_id: string;
  snapshot: MfgDealCogsSnapshotRow;
};

export function getManufacturingDealMaterialsPreview(dealId: string, opts?: { sale_price_usd?: number }) {
  const q = new URLSearchParams({ deal_id: dealId });
  if (opts?.sale_price_usd != null && Number.isFinite(opts.sale_price_usd)) {
    q.set("sale_price_usd", String(opts.sale_price_usd));
  }
  return manufacturingFetch<MfgDealMaterialsPreviewResponse>(`/api/mfg/orders/materials-preview?${q.toString()}`, {
    method: "GET",
  });
}

export function getManufacturingDealCogsSnapshot(dealId: string, snapshotKind = "sale_commit") {
  const q = new URLSearchParams({ deal_id: dealId, snapshot_kind: snapshotKind });
  return manufacturingFetch<MfgDealCogsSnapshotGetResponse>(`/api/mfg/orders/cogs-snapshot?${q.toString()}`, {
    method: "GET",
  });
}

export function getManufacturingDealFullCogsPreview(dealId: string, opts?: { sale_price_usd?: number }) {
  const q = new URLSearchParams({ deal_id: dealId });
  if (opts?.sale_price_usd != null && Number.isFinite(opts.sale_price_usd)) {
    q.set("sale_price_usd", String(opts.sale_price_usd));
  }
  return manufacturingFetch<MfgDealFullCogsPreviewResponse>(`/api/mfg/orders/full-cogs-preview?${q.toString()}`, {
    method: "GET",
  });
}

export function commitManufacturingDealCogsSnapshot(
  dealId: string,
  opts?: { snapshot_kind?: "sale_commit" | "build_commit" | "materials_commit"; sale_price_usd?: number },
) {
  return manufacturingFetch<MfgDealCogsSnapshotCreateResponse>("/api/mfg/orders/cogs-snapshot/commit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      deal_id: dealId,
      snapshot_kind: opts?.snapshot_kind ?? "sale_commit",
      sale_price_usd: opts?.sale_price_usd,
    }),
  });
}
